import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import * as Analytics from 'src/services/analytics/analytics';
import * as GoogleAds from 'src/services/analytics/googleAds';

/**
 * Router state passed from the register form to /register/thank-you.
 *
 * `signupCompleted` is true only on the navigation that follows a successful
 * registration, and is cleared once the signup has been tracked.
 */
export interface ThankYouLocationState {
  email?: string;
  openSignUp?: boolean;
  signupCompleted?: boolean;
}

/**
 * Fires the GA4 `sign_up` event and the Google Ads signup conversion once per
 * completed registration.
 *
 * Browser history keeps router state across reloads and back/forward
 * navigation, so firing on every mount would count the same signup again.
 * After tracking, the current history entry is replaced with
 * `signupCompleted: false`, leaving the rest of the state intact for the page.
 * Direct visits carry no state and are never tracked.
 */
export function useSignupConversionTracking(): void {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const state = (location.state as ThankYouLocationState | null) ?? {};

    if (!state.signupCompleted) return;

    Analytics.trackSignupEvent();
    GoogleAds.trackSignupConversion();

    navigate(location, {
      replace: true,
      state: { ...state, signupCompleted: false }
    });
  }, []);
}
